/* Escaping and date formatting.
   =============================

   The second extraction, and the most-used code in the app: escapeHtml and
   escapeAttr are called 286 times between them, todayISO and prettyDate 155.

   escapeHtml is SECURITY CODE. Nearly every list, card and table in this app is
   built by string concatenation into innerHTML, and this function is the only
   thing standing between a client's name and script execution. A CHW typing
   O'Brien, or a transcript containing <b>, must not be able to change the shape
   of the page. That is why it is here with tests rather than sitting inline
   among 27,000 other lines.

   Moved unchanged, deliberately. See the note on prettyDate below for the one
   place where the existing behaviour is arguably wrong — that is recorded as a
   test rather than quietly fixed, because moving code and changing code are two
   different jobs and doing both at once means neither can be verified.
*/

/* Escapes the five characters that can break out of HTML text or an attribute.
   Single quotes matter as much as double: plenty of the markup in this app is
   built with '...' delimiters. */
export function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

/* Same escaping. Kept as a separate name because the call sites say what they
   mean — escapeAttr inside an attribute, escapeHtml in text — and if the two
   ever need to diverge, every call site is already labelled. */
export function escapeAttr(s) {
  return escapeHtml(s);
}

/* Today as YYYY-MM-DD in LOCAL time. Not toISOString(), which converts to UTC
   and hands back yesterday for anyone west of Greenwich after 4pm — which is
   every user of this app, since it is used in California. */
export function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/* "2026-08-17" -> "Mon, Aug 17, 2026".
   Built from parts rather than new Date(iso) on purpose: parsing a bare
   date string is treated as UTC, which shifts the displayed day backwards in
   any negative timezone.

   Returns '' for anything it cannot read. It used to return the literal string
   "Invalid Date", and several call sites pass
   `(record.updatedAt || '').slice(0, 10)` — so any record missing a timestamp
   put "Invalid Date" on a card in front of a CHW. An empty space says "no date
   recorded"; "Invalid Date" says "this app is broken". */
export function prettyDate(iso) {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  if (!y || !m || !d || isNaN(y) || isNaN(m) || isNaN(d)) return '';
  if (m < 1 || m > 12 || d < 1 || d > 31) return '';
  const dt = new Date(y, m - 1, d);
  /* Date ROLLS OVER rather than rejecting: month 13 becomes January of the
     next year, and 31 February becomes 3 March. So a bad date does not
     announce itself — it quietly becomes a different, plausible date. Reading
     the parts back is the only way to catch it, and showing nothing beats
     showing a date that was never in the record. */
  if (isNaN(dt.getTime()) || dt.getMonth() !== m - 1 || dt.getDate() !== d) return '';
  return dt.toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
  });
}
